import type { InjectionKey, Ref } from 'vue'

/**
 * A single administration menu destination. Carries a stable slug `key`
 * — the modal's active panel key and the `?modals=admin:<key>` deep-link
 * value.
 */
export interface AdminNavTarget {
  /** Stable slug — modal active key and `?modals=admin:` deep-link value. */
  readonly key: string
}

/**
 * Canonical destinations. Single source of truth for the slug set, shared
 * between the menu (explicit per-item markup) and the modal (active-key
 * typing + deep-link validation).
 */
export const ADMIN_TARGETS = {
  tenantUsers: { key: 'tenant-users' },
  accessRoles: { key: 'access-roles' },
  agencyStructure: { key: 'agency-structure' },
  companies: { key: 'companies' },
  currencies: { key: 'currencies' },
  customFields: { key: 'custom-fields' },
  estimatedTimeNames: { key: 'estimated-time-names' },
  portcallTypes: { key: 'portcall-types' },
  agencySettings: { key: 'agency-settings' }
} satisfies Record<string, AdminNavTarget>

/** Slug of any administration menu destination. */
export type AdminPanelKey =
  (typeof ADMIN_TARGETS)[keyof typeof ADMIN_TARGETS]['key']

/**
 * Navigation strategy consumed by `AdministrationMainMenu`. The
 * administration menu now lives exclusively inside the central
 * `AdministrationModal`, so the only strategy is the modal panel switcher
 * (the retired route-driven section is gone — central-modal-host Phase 7).
 */
export interface AdminMenuNav {
  /** Inclusive match — drives the highlight (`data-active`). */
  isItemActive(target: AdminNavTarget): boolean
  /** Exact match — drives the indicator bar (`data-exact-active`). */
  isItemExactActive(target: AdminNavTarget): boolean
  /** Any child active — drives a collapsible group's auto-open. */
  isGroupActive(targets: readonly AdminNavTarget[]): boolean
  /** Anchor href; always `null` now — items render as buttons. */
  hrefFor(target: AdminNavTarget): string | null
  /** Click switches the modal's active panel. */
  activate(event: MouseEvent, target: AdminNavTarget): void
}

export const adminMenuNavKey: InjectionKey<AdminMenuNav> =
  Symbol('adminMenuNav')

/**
 * Modal nav strategy: the menu becomes a panel switcher bound to the
 * modal's active-key ref. Items render as buttons (no href) and clicking
 * sets the active panel.
 */
export function createModalAdminMenuNav(
  active: Ref<AdminPanelKey>
): AdminMenuNav {
  const matches = (target: AdminNavTarget) => active.value === target.key
  return {
    isItemActive: matches,
    isItemExactActive: matches,
    isGroupActive: (targets) => targets.some(matches),
    hrefFor: () => null,
    activate(_event, target) {
      active.value = target.key as AdminPanelKey
    }
  }
}
